import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { BaseStitchListComponent, StitchListItem } from '@modules/common/components/list/base-stitch-list.component';
import { ScrollToPosition } from '@modules/common/services/scroll-to-index.injection-token';
import { type StitchCardSelection, type StitchCardVariant } from '@modules/common/utils/stitch-card-variant';
import { SheetFolderComponent } from '@modules/elements/components/sheet-folder/sheet-folder.component';
import { type SelectableOpenEvent } from '@modules/drag-n-drop/types/selectable-open-event';
import { LinkedInfoService } from '@modules/linked-info/services/linked-info.service';
import { SheetFoldersService } from '@modules/sheets/services/sheet-folders.service';
import { SheetsAppStateService } from '@modules/sheets/services/state.service';
import { SHEETS_STRINGS } from '@sharedlib/sheets-editor/sheets.strings';
import { Sheet } from '@modules/sheets/types/sheet';
import { SheetFolder } from '@modules/sheets/types/sheet-folder';
import { SheetFoldersFilters } from '@modules/sheets/types/sheet-folders-filters';
import type { KnotSource } from '@sharedlib/common/types/knowledge/knot-source';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { BehaviorSubject, merge, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-sheet-folders-list',
  templateUrl: './sheet-folders-list.component.html',
  styleUrls: ['./sheet-folders-list.component.less'],
  providers: [{ provide: ScrollToPosition, useFactory: () => new BehaviorSubject<number>(null) }],
  standalone: false,
})
export class SheetFoldersListComponent extends BaseStitchListComponent<SheetFolder> implements OnInit, OnDestroy, OnChanges {
  /** Show each row's hover quick actions; off for lists that only preview their items. */
  public readonly actionsButtonEnabled = input<boolean>(true);

  /** Container treatment forwarded to every card; the knowledge panel asks for `'card'`. */
  public readonly variant = input<StitchCardVariant>('row');

  /** Id of the item open in the dynamic panel, forwarded so that card draws itself as selected. */
  public readonly previewedItemId = input<string | null>(null);

  /** What each card draws as selected, forwarded; the knowledge panel asks for the list's own selection. */
  public readonly cardSelection = input<StitchCardSelection>('preview');

  /** The knowledge panel draws its results as cards; every other caller keeps the app's flush row. */
  protected readonly isCard = computed(() => this.variant() === 'card');

  /** NFR-8: the list's name, among the template's other words, comes from the Sheets strings. */
  protected readonly S = SHEETS_STRINGS;

  @Input()
  public override filters: Partial<SheetFoldersFilters> = {};
  @Input()
  public withTags = false;
  @Input()
  public withKnots = false;
  @Input()
  public knotsSource: KnotSource;
  @Input()
  public debug: 'score';

  @Output()
  public readonly clickSheetFolder = new EventEmitter<SheetFolder>();
  @Output()
  public readonly openSheetFolder = new EventEmitter<SheetFolder>();
  @Output()
  public readonly doubleClickSheetFolder = new EventEmitter<SheetFolder>();
  @Output()
  public readonly openSheet = new EventEmitter<Sheet>();

  @ViewChildren(SheetFolderComponent, { read: ElementRef<HTMLElement> })
  public override itemElements: QueryList<ElementRef<HTMLElement>>;

  public override items: StitchListItem<SheetFolder>[] = [];

  private readonly sheetFoldersService = inject(SheetFoldersService);
  private readonly sheetsStateService = inject(SheetsAppStateService);
  private readonly linkedInfoService = inject(LinkedInfoService);

  protected override getStitchType(): StitchType {
    return StitchType.SHEET_FOLDER;
  }

  public override ngOnInit() {
    merge(
      // Global refresh button
      this.sheetsStateService.getRefreshAll(),
      // Subscribe for linked info updates
      this.linkedInfoService.getRefresh(),
      this.sheetFoldersService.getRefresh(),
    )
      .pipe(takeUntil(this.alive))
      .subscribe(() => {
        this.loadPage(this.page);
      });


    super.ngOnInit();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if ('filters' in changes) {
      this.resetItems();
    }
  }

  protected override getItems(offset: number, limit: number): Observable<{ items: SheetFolder[]; count: number }> {
    return this.sheetFoldersService.search(
      { ...this.filters, limit, offset },
      { withTags: this.withTags, withKnots: this.withKnots, withConnections: false, knotsSource: this.knotsSource },
    );
  }

  public handleDoubleClickItem(sheetFolder: SheetFolder) {
    this.doubleClickSheetFolder.emit(sheetFolder);
  }

  public handleOpenSelected(event: SelectableOpenEvent<SheetFolder>): void {
    this.clickSheetFolder.emit(event.item.data);
  }
}
